import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import {
    Button,
    Card,
    CardContent,
    Typography,
    TextField,
    Box,
    Alert,
    Divider,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    CalendarToday,
    Save,
    Cancel,
    Add,
    Edit,
    Delete
} from '@mui/icons-material';

const CompanyEdit = ({ match }) => {
    const recordId = match.params.id; // Assuming you're using React Router
    const history = useHistory();
    const [pinCodes, setPinCodes] = useState([]);

    const { isAuthenticated, companyId, permissions, isSuperAdmin, superCompanyId, logout, token } = useAuth();
    useEffect(() => {
        // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            // Token is expired, log out the user
            logout();
            return;
        }

    }, [isAuthenticated, companyId, token, logout]);

    const [formData, setFormData] = useState({
        Name: '',
        CompanyCode: '',
        ContactNumber1: '',
        ContactNumber2: '',
        Email: '',
        GSTNumber: '',
        AddressLine1: '',
        AddressLine2: '',
        PinCode: '',
        City: '',
        District: '',
        State: '',
        Country: '',
        BillRemarks: '',
        ReprintPassword: '',
        PaymentQRUPIId: '',
        PaymentQRUPIName: '',
        PromoDiscountStartDate: '',
        PromoDiscountEndDate: '',
        PromoDiscountSlab: [], // New field replacing PromoDiscountQuantity
    });

    const [dateValidationError, setDateValidationError] = useState('');
    const [slabValidationError, setSlabValidationError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    
    // Slab management state
    const [showSlabDialog, setShowSlabDialog] = useState(false);
    const [editingSlabIndex, setEditingSlabIndex] = useState(null);
    const [currentSlab, setCurrentSlab] = useState({
        DiscountPercentage: '',
        MinimumQuantity: ''
    });

    const companyid = 1;
    
    // Slab validation utilities
    const validateSlab = (slab, existingSlabs = [], editingIndex = null) => {
        const errors = {};
        
        // Required fields validation
        if (!slab.DiscountPercentage || slab.DiscountPercentage === '') {
            errors.DiscountPercentage = 'Discount percentage is required';
        }
        if (!slab.MinimumQuantity || slab.MinimumQuantity === '') {
            errors.MinimumQuantity = 'Minimum quantity is required';
        }
        
        // Discount percentage validation (1-100)
        const discountPercent = parseInt(slab.DiscountPercentage);
        if (slab.DiscountPercentage && (isNaN(discountPercent) || discountPercent < 1 || discountPercent > 100)) {
            errors.DiscountPercentage = 'Discount percentage must be between 1 and 100';
        }
        
        // Minimum quantity validation (> 0)
        const minQuantity = parseInt(slab.MinimumQuantity);
        if (slab.MinimumQuantity && (isNaN(minQuantity) || minQuantity < 1)) {
            errors.MinimumQuantity = 'Minimum quantity must be greater than 0';
        }
        
        // Duplicate MinimumQuantity validation
        if (slab.MinimumQuantity && !errors.MinimumQuantity) {
            const duplicateIndex = existingSlabs.findIndex((existingSlab, index) => 
                index !== editingIndex && parseInt(existingSlab.MinimumQuantity) === minQuantity
            );
            if (duplicateIndex !== -1) {
                errors.MinimumQuantity = `Minimum quantity ${minQuantity} already exists`;
            }
        }
        
        return errors;
    };
    
    // Slab management functions
    const openSlabDialog = (slabIndex = null) => {
        if (slabIndex !== null) {
            // Editing existing slab
            setEditingSlabIndex(slabIndex);
            setCurrentSlab(formData.PromoDiscountSlab[slabIndex]);
        } else {
            // Adding new slab
            setEditingSlabIndex(null);
            setCurrentSlab({ DiscountPercentage: '', MinimumQuantity: '' });
        }
        setSlabValidationError('');
        setShowSlabDialog(true);
    };
    
    const closeSlabDialog = () => {
        setShowSlabDialog(false);
        setEditingSlabIndex(null);
        setCurrentSlab({ DiscountPercentage: '', MinimumQuantity: '' });
        setSlabValidationError('');
    };
    
    const saveSlab = () => {
        const errors = validateSlab(currentSlab, formData.PromoDiscountSlab, editingSlabIndex);
        
        if (Object.keys(errors).length > 0) {
            setSlabValidationError(Object.values(errors).join(', '));
            return;
        }
        
        const updatedSlabs = [...formData.PromoDiscountSlab];
        const slabData = {
            DiscountPercentage: parseInt(currentSlab.DiscountPercentage),
            MinimumQuantity: parseInt(currentSlab.MinimumQuantity)
        };
        
        if (editingSlabIndex !== null) {
            // Update existing slab
            updatedSlabs[editingSlabIndex] = slabData;
        } else {
            // Add new slab
            updatedSlabs.push(slabData);
        }
        
        // Sort slabs by MinimumQuantity for better display
        updatedSlabs.sort((a, b) => a.MinimumQuantity - b.MinimumQuantity);
        
        setFormData(prevData => ({
            ...prevData,
            PromoDiscountSlab: updatedSlabs
        }));
        
        closeSlabDialog();
    };
    
    const deleteSlab = (slabIndex) => {
        const updatedSlabs = [...formData.PromoDiscountSlab];
        updatedSlabs.splice(slabIndex, 1);
        setFormData(prevData => ({
            ...prevData,
            PromoDiscountSlab: updatedSlabs
        }));
    };
    
    const handleSlabInputChange = (field, value) => {
        setCurrentSlab(prev => ({
            ...prev,
            [field]: value
        }));
    };
    useEffect(() => {
        fetchData('PinCodeApi', `/${companyid}`, token)
            .then((response) => {
                if (response.responseCode == '1111') {
                    setPinCodes(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching PIN codes:', error);
            });
    }, []);

    const handlePinCodeChange = (event) => {
        const value = event;
        if (value.length === 6) {
            const selectedPinCode = pinCodes.find(pinCode => pinCode.Postcode === value);
            if (selectedPinCode) {
                setFormData(prevData => ({
                    ...prevData,
                    City: selectedPinCode.City,
                    District: selectedPinCode.District,
                    State: selectedPinCode.State,
                    Country: selectedPinCode.Country
                }));
            }
        }
    };

    useEffect(() => {
        setIsLoading(true);
        fetchData('CompanyApi', `/CompanyDetails/${recordId}`, token)
            .then((response) => {
                if (response.responseCode == '1111') {
                    const responseData = response.response_data;
                    
                    // Handle new CompanyVM structure
                    let companyData;
                    let promoDiscountSlab = [];
                    
                    if (responseData.Company) {
                        // New structure: CompanyVM with separate Company and PromoDiscountSlab
                        companyData = responseData.Company;
                        promoDiscountSlab = responseData.PromoDiscountSlab || [];
                    } else {
                        // Old structure: Direct company data (backward compatibility)
                        companyData = responseData;
                        promoDiscountSlab = responseData.PromoDiscountSlab || [];
                    }
                    
                    // Ensure promotional date fields exist (backward compatibility)
                    if (!companyData.hasOwnProperty('PromoDiscountStartDate')) {
                        companyData.PromoDiscountStartDate = '';
                    }
                    if (!companyData.hasOwnProperty('PromoDiscountEndDate')) {
                        companyData.PromoDiscountEndDate = '';
                    }
                    
                    // Migration logic: Convert old PromoDiscountQuantity to PromoDiscountSlab
                    if (promoDiscountSlab.length === 0 && companyData.PromoDiscountQuantity) {
                        // Create default slab from existing quantity
                        promoDiscountSlab = [{
                            DiscountPercentage: 0, // Default to 0% - user must configure
                            MinimumQuantity: parseInt(companyData.PromoDiscountQuantity)
                        }];
                    }
                    
                    // Convert date formats for HTML date inputs
                    if (companyData.PromoDiscountStartDate) {
                        companyData.PromoDiscountStartDate = formatDateForInput(companyData.PromoDiscountStartDate);
                    }
                    if (companyData.PromoDiscountEndDate) {
                        companyData.PromoDiscountEndDate = formatDateForInput(companyData.PromoDiscountEndDate);
                    }
                    
                    // Combine company data with PromoDiscountSlab for form state
                    const formDataToSet = {
                        ...companyData,
                        PromoDiscountSlab: promoDiscountSlab
                    };
                    
                    setFormData(formDataToSet);
                }
            })
            .catch((error) => {
                console.error('Error fetching Company Details:', error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    // Helper function to format date for HTML date input (YYYY-MM-DD)
    const formatDateForInput = (dateString) => {
        if (!dateString || dateString === null || dateString === undefined) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '';
            
            // Use local date components to avoid timezone conversion issues
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            
            return `${year}-${month}-${day}`;
        } catch (error) {
            console.warn('Error formatting date for input:', dateString, error);
            return '';
        }
    };


    const handleChange = event => {
        const { name, value } = event.target;
        console.log(name, value); // Add this line to debug
        setFormData(prevData => ({
            ...prevData,
            [name]: value,
        }));

        // Validate promotional dates
        if (name === 'PromoDiscountStartDate' || name === 'PromoDiscountEndDate') {
            validatePromotionalDates(name, value);
        }
    };

    const validatePromotionalDates = (changedField, changedValue) => {
        const startDate = changedField === 'PromoDiscountStartDate' ? changedValue : formData.PromoDiscountStartDate;
        const endDate = changedField === 'PromoDiscountEndDate' ? changedValue : formData.PromoDiscountEndDate;

        // Clear previous errors
        setDateValidationError('');

        // Only validate if both dates are provided
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            
            // Check if dates are valid
            if (isNaN(start.getTime()) || isNaN(end.getTime())) {
                setDateValidationError('Please enter valid dates');
                return;
            }
            
            // Check if end date is after start date
            if (end <= start) {
                setDateValidationError('End date must be after start date');
                return;
            }
            
            // Optional: Check if dates are not too far in the past (uncomment if needed)
            // const today = new Date();
            // today.setHours(0, 0, 0, 0);
            // if (start < today) {
            //     setDateValidationError('Start date cannot be in the past');
            //     return;
            // }
        }
        
        // If we get here, validation passed
        setDateValidationError('');
    };

    const handleSubmit = event => {
        event.preventDefault();

        if (dateValidationError) {
            return;
        }

        setIsLoading(true);

        const Company = { ...formData };
        
        // Extract PromoDiscountSlab from Company data
        const PromoDiscountSlab = Company.PromoDiscountSlab || [];
        delete Company.PromoDiscountSlab; // Remove from Company object
        
        const submissionData = {
            Company: {
                ...Company,
                UpdatedBy: 1,
            },
            PromoDiscountSlab: PromoDiscountSlab
        };

        try {
            if (submissionData.Company.PromoDiscountStartDate && submissionData.Company.PromoDiscountStartDate.trim() !== '') {
                // Create date in local timezone to avoid timezone conversion issues
                const startDate = new Date(submissionData.Company.PromoDiscountStartDate + 'T00:00:00');
                submissionData.Company.PromoDiscountStartDate = startDate.toISOString();
            } else {
                submissionData.Company.PromoDiscountStartDate = null;
            }
            if (submissionData.Company.PromoDiscountEndDate && submissionData.Company.PromoDiscountEndDate.trim() !== '') {
                // Create date in local timezone to avoid timezone conversion issues
                const endDate = new Date(submissionData.Company.PromoDiscountEndDate + 'T00:00:00');
                submissionData.Company.PromoDiscountEndDate = endDate.toISOString();
            } else {
                submissionData.Company.PromoDiscountEndDate = null;
            }
            
            // Ensure PromoDiscountSlab is properly formatted for API
            if (submissionData.PromoDiscountSlab && submissionData.PromoDiscountSlab.length > 0) {
                submissionData.PromoDiscountSlab = submissionData.PromoDiscountSlab.map(slab => ({
                    DiscountPercentage: parseInt(slab.DiscountPercentage),
                    MinimumQuantity: parseInt(slab.MinimumQuantity)
                }));
            } else {
                submissionData.PromoDiscountSlab = [];
            }
            
            // Remove the old PromoDiscountQuantity field from Company
            delete submissionData.Company.PromoDiscountQuantity;
            
        } catch (error) {
            setDateValidationError('Invalid date format. Please check your promotional dates.');
            setIsLoading(false);
            return;
        }

        updateData('CompanyApi', `${recordId}`, submissionData, token)
            .then((response) => {
                console.log('Record updated successfully:', response);
                history.push('/Home');
            })
            .catch((error) => {
                console.error('Error updating record:', error);
                setDateValidationError('Error updating company information. Please try again.');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const phoneNumberPattern = /^\d{10}$/;
    const emailPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/;

    return (
        <Box sx={{ 
            minHeight: '100vh',
            backgroundColor: '#f8f9fa',
            padding: 3,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            paddingTop: 6
        }}>
            <Card sx={{ 
                width: '100%',
                maxWidth: 600,
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                borderRadius: 2
            }}>
                <CardContent sx={{ padding: 4 }}>
                    {/* Header */}
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mb: 3
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CalendarToday sx={{ color: '#1976d2', fontSize: 24 }} />
                            <Typography variant="h5" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                                Promotional Discount Settings
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Form Content */}
                    <Box component="form" onSubmit={handleSubmit}>
                        <Typography variant="h6" sx={{ mb: 2, color: '#34495e', fontWeight: 500 }}>
                            Discount Period Configuration
                        </Typography>
                        
                        <Box sx={{ 
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 3,
                            mb: 3
                        }}>
                            <TextField
                                label="Promotion Start Date"
                                name="PromoDiscountStartDate"
                                type="date"
                                value={formData.PromoDiscountStartDate}
                                onChange={handleChange}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                helperText="Select when the promotion begins"
                                variant="outlined"
                            />

                            <TextField
                                label="Promotion End Date"
                                name="PromoDiscountEndDate"
                                type="date"
                                value={formData.PromoDiscountEndDate}
                                onChange={handleChange}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                helperText="Select when the promotion ends"
                                variant="outlined"
                            />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, color: '#34495e', fontWeight: 500 }}>
                                Discount Slabs Configuration
                            </Typography>
                            
                            {/* Discount Slabs Table */}
                            <Box sx={{ mb: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Discount Slabs ({formData.PromoDiscountSlab?.length || 0})
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<Add />}
                                        onClick={() => openSlabDialog()}
                                        size="small"
                                        disabled={isLoading}
                                        sx={{ py: 0.5, px: 1.5, fontSize: '0.75rem' }}
                                    >
                                        Add Slab
                                    </Button>
                                </Box>
                                
                                {formData.PromoDiscountSlab && formData.PromoDiscountSlab.length > 0 ? (
                                    <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                                        <Table size="small">
                                            <TableHead sx={{ backgroundColor: '#58A5B3' }}>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold', color: 'white', py: 1, fontSize: '0.8rem' }}>
                                                        Min Qty
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: 'white', py: 1, fontSize: '0.8rem' }}>
                                                        Discount %
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: 'white', width: 80, py: 1, fontSize: '0.8rem' }}>
                                                        Actions
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody sx={{ backgroundColor: '#C3E9E4' }}>
                                                {formData.PromoDiscountSlab.map((slab, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>{slab.MinimumQuantity}</TableCell>
                                                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>{slab.DiscountPercentage}%</TableCell>
                                                        <TableCell sx={{ py: 0.5 }}>
                                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => openSlabDialog(index)}
                                                                    disabled={isLoading}
                                                                    sx={{ p: 0.5 }}
                                                                >
                                                                    <Edit fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => deleteSlab(index)}
                                                                    disabled={isLoading}
                                                                    color="error"
                                                                    sx={{ p: 0.5 }}
                                                                >
                                                                    <Delete fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Box sx={{ 
                                        textAlign: 'center', 
                                        py: 2, 
                                        border: '1px dashed #ddd', 
                                        borderRadius: 1,
                                        backgroundColor: '#f9f9f9'
                                    }}>
                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                                            No discount slabs configured
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            startIcon={<Add />}
                                            onClick={() => openSlabDialog()}
                                            disabled={isLoading}
                                            size="small"
                                            sx={{ py: 0.5, px: 1.5, fontSize: '0.75rem' }}
                                        >
                                            Add First Slab
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Error Display */}
                        {dateValidationError && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {dateValidationError}
                            </Alert>
                        )}

                        {/* Action Buttons */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 2, 
                            justifyContent: 'flex-end',
                            mt: 4
                        }}>
                            <Button
                                variant="outlined"
                                component={Link}
                                to="/Home"
                                startIcon={<Cancel />}
                                disabled={isLoading}
                            >
                                Cancel
                            </Button>
                            
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={isLoading || !!dateValidationError}
                                startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
                            >
                                {isLoading ? 'Updating...' : 'Update'}
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
            
            {/* Slab Add/Edit Dialog */}
            <Dialog open={showSlabDialog} onClose={closeSlabDialog} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ pb: 1 }}>
                    {editingSlabIndex !== null ? 'Edit Discount Slab' : 'Add Discount Slab'}
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Box sx={{ pt: 1 }}>
                        <TextField
                            label="Discount Percentage"
                            type="number"
                            value={currentSlab.DiscountPercentage}
                            onChange={(e) => handleSlabInputChange('DiscountPercentage', e.target.value)}
                            fullWidth
                            inputProps={{ min: 1, max: 100, step: 1 }}
                            helperText="Enter discount percentage (1-100)"
                            sx={{ mb: 1.5 }}
                            variant="outlined"
                            size="small"
                        />
                        <TextField
                            label="Minimum Quantity"
                            type="number"
                            value={currentSlab.MinimumQuantity}
                            onChange={(e) => handleSlabInputChange('MinimumQuantity', e.target.value)}
                            fullWidth
                            inputProps={{ min: 1, step: 1 }}
                            helperText="Enter minimum quantity required for this discount"
                            variant="outlined"
                            size="small"
                        />
                        
                        {slabValidationError && (
                            <Alert severity="error" sx={{ mt: 1.5 }}>
                                {slabValidationError}
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ pt: 1, pb: 2 }}>
                    <Button onClick={closeSlabDialog} disabled={isLoading} size="small">
                        Cancel
                    </Button>
                    <Button 
                        onClick={saveSlab} 
                        variant="contained" 
                        disabled={isLoading}
                        size="small"
                    >
                        {editingSlabIndex !== null ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CompanyEdit;
